import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { CellRendererProps, FlatList, useWindowDimensions } from 'react-native';
import Animated, { useAnimatedScrollHandler, useSharedValue } from 'react-native-reanimated';
import { useSafeAreaInsets } from 'react-native-safe-area-context';

import { useNavigation, useScrollToTop } from '@react-navigation/native';

import { TABS_HEIGHT } from '@/components/LeftSideTabBar';
import { LobbyProductSwitchButton, useLogoHeaderHeight } from '@/components/LobbyProductSwitchButton';
import { SizedBox } from '@/components/SizedBox';
import { BOTTOM_TABS_HEIGHT } from '@/components/TabBar';
import { Box } from '@/components/lib/components';
import { PlayerWithTeamAndEvent } from '@/feature/betslip-pickem/types';
import { LEAGUES_LIST_HEIGHT, LobbyLeagues } from '@/feature/lobby/components/LobbyLeagues';
import { useFantasyLobbyContent } from '@/feature/lobby/hooks/use-fantasy-lobby-content';
import { lobbyId, useLobbySectionSelection } from '@/feature/lobby/hooks/use-lobby-section-selection';
import { MaxWidthWrapper } from '@/feature/responsive-design/WebComponents';
import { SEARCH_CONTAINER_HEIGHT, SearchContainer } from '@/feature/search/components/SearchContainer';
import { useJurisdictionStore } from '@/hooks/use-jurisdiction';
import { usePrevious } from '@/hooks/use-previous';
import { PICKEM_TAB_BAR_HEIGHT } from '@/navigation/pickem/tabs';
import { PickemLobbyTabNavigationProp } from '@/navigation/pickem/types';
import { League } from '@/types/api.generated';
import { ProductDocumentDataBodyLobbycomponentsSliceItem } from '@/types/prismic.generated';
import { isWeb, showScrollIndicator } from '@/utils/constants-platform-specific';

import { usePlayerFilters } from '../hooks/use-player-filters';
import { StickyWrapper } from '../hooks/use-sticky-translation';
import { DEFAULT_LOBBY_ORDERING, LobbyComponent } from '../utils/lobbySections';
import { StickyCellRendererComponent } from './sticky-item/StickyCellRendererComponent';

export const LAUNCHPAD_HEIGHT_SEARCH_ONLY = 68;

const LAUNCHPAD_SCROLLED_BOTTOM_OFFSET = 12;

type LobbyListContent = LobbyLeagueSection | ProductDocumentDataBodyLobbycomponentsSliceItem | PlayerWithTeamAndEvent;

export type LobbyLeagueSection =
    | 'logo'
    | 'search'
    | 'leagues'
    | 'eventsCards'
    | 'filters'
    | keyof typeof LobbyComponent;

export const LobbyLeagueComponents = {
    Lobby: ['logo', 'search', 'leagues'],
    League: ['logo', 'search', 'leagues', 'eventsCards', 'filters'],
    ExpandedEvent: ['filters'],
} as const;

/**
 * This is the list of the elements on the lobby/leagues screen
 * If selectedTile is `lobby` we display the CommonComponents + Lobby components:
 *  - ['logo', 'search', 'leagues', 'lobbyContent']
 * If selectedTile is a League we display the CommonComponents + League components + List of players/projections
 * - ['logo', 'search', 'leagues', 'eventsCards', 'filters'] + players's row/tile
 */
export const LobbyList = ({ league }: { league: League }) => {
    const { listRef, filtersStickPosition, listScrollOffset: scrollOffset } = usePlayerFilters();
    useScrollToTop(listRef);

    const configuredComponents = useJurisdictionStore(
        state => state.jurisdictionSettings?.productConfig?.lobbyComponents?.items ?? DEFAULT_LOBBY_ORDERING
    );

    const lobbyComponents = useMemo(() => {
        return configuredComponents.filter(c => c.enabled).filter(Boolean);
    }, [configuredComponents]);

    const { selectedTile } = useLobbySectionSelection();
    const { listMinHeight, leaguesOffset } = useElementsDimensions();

    const prevSelectedTile = usePrevious(selectedTile);

    const isLobby = selectedTile === 'lobby';
    const [listToRender, setListToRender] = useState<readonly LobbyListContent[]>(
        isLobby ? LobbyLeagueComponents.Lobby : LobbyLeagueComponents.League
    );

    const setSelectedTile = useLobbySectionSelection(state => state.actions.setSelectedTile);
    const navigation = useNavigation<PickemLobbyTabNavigationProp>();

    const scrollVelocity = useSharedValue(0);
    const leaguesStickPosition = useSharedValue(0);
    const scrollDirection = useSharedValue<'up' | 'down'>('down');

    const onScroll = useAnimatedScrollHandler<{ prevY: number }>(
        {
            onBeginDrag: (event, ctx) => {
                scrollVelocity.value = 0;
                ctx.prevY = event.contentOffset.y;
            },
            onScroll: (event, context) => {
                const currentY = event.contentOffset.y;

                scrollOffset.value = currentY;

                if (currentY < context?.prevY) {
                    scrollDirection.value = 'up';
                } else if (currentY > context?.prevY) {
                    scrollDirection.value = 'down';
                }

                context.prevY = currentY;
            },

            onEndDrag: event => {
                // save velocity to be used in the sticky wrapper
                scrollVelocity.value = event?.velocity?.y || 0;
            },
        },
        [scrollVelocity, scrollOffset, scrollDirection]
    );

    const {
        sortedPlayers,
        sortedLobbyPlayers,
        renderLobbyPlayerItem,
        renderLobbyComponent,
        renderPlayerItem,
        renderEventCards,
        renderFilters,
        renderLeagueFooter,
    } = useFantasyLobbyContent({
        selectedTile,
    });

    useEffect(() => {
        if (isLobby) {
            setListToRender([...LobbyLeagueComponents.Lobby, ...lobbyComponents, ...sortedLobbyPlayers]);
        } else {
            setListToRender([...LobbyLeagueComponents.League, ...sortedPlayers]);
        }
    }, [sortedPlayers, isLobby, lobbyComponents, sortedLobbyPlayers]);

    useEffect(() => {
        if (scrollOffset.value > leaguesOffset && prevSelectedTile !== selectedTile) {
            listRef?.current?.scrollToOffset({ animated: false, offset: leaguesOffset });
        }
    }, [leaguesOffset, listRef, prevSelectedTile, scrollOffset, selectedTile]);

    useEffect(() => {
        if (league) {
            setSelectedTile(league);
        } else {
            setSelectedTile(lobbyId);
            navigation.setParams({
                league: lobbyId,
            });
        }
    }, [league, navigation, setSelectedTile]);

    const renderListItem = useCallback(
        ({ item, index }: { item: LobbyListContent; index: number }) => {
            if (typeof item === 'string') {
                if (item === 'logo') {
                    return <LobbyProductSwitchButton />;
                }
                if (item === 'search') {
                    return (
                        <MaxWidthWrapper>
                            <SearchContainer />
                        </MaxWidthWrapper>
                    );
                }
                if (item === 'leagues') {
                    return (
                        <StickyWrapper
                            scrollDirection={scrollDirection}
                            scrollOffset={scrollOffset}
                            initialPosition={leaguesStickPosition}
                            scrollVelocity={scrollVelocity}
                        >
                            <Box backgroundColor={'gray8'}>
                                <LobbyLeagues />
                            </Box>
                        </StickyWrapper>
                    );
                }
                if (item === 'eventsCards') {
                    return renderEventCards();
                }
                if (item === 'filters') {
                    return (
                        <StickyWrapper
                            scrollDirection={scrollDirection}
                            scrollOffset={scrollOffset}
                            initialPosition={filtersStickPosition}
                            scrollVelocity={scrollVelocity}
                        >
                            <Box backgroundColor={'gray8'}>{renderFilters()}</Box>
                        </StickyWrapper>
                    );
                }
                console.warn('Unknown item type in LobbyList:', item);
                return null;
            }
            if (typeof item === 'object' && 'orderingkey' in item) {
                return <MaxWidthWrapper key={item.orderingkey}>{renderLobbyComponent(item)}</MaxWidthWrapper>;
            }
            if (isLobby) {
                //For the lobby we render the lobby player item component and also pass the title so we can render it
                //above the first player in the lobby list
                const title = lobbyComponents.find(c => c.orderingkey === LobbyComponent.FeaturedPlayersList)?.header;
                return (
                    <MaxWidthWrapper key={item.id}>
                        {renderLobbyPlayerItem({ item, index, title: title || '' })}
                    </MaxWidthWrapper>
                );
            }
            return renderPlayerItem({ item, index, ignoreIndexes: LobbyLeagueComponents.League.length });
        },
        [
            isLobby,
            renderPlayerItem,
            scrollDirection,
            scrollOffset,
            leaguesStickPosition,
            scrollVelocity,
            renderEventCards,
            filtersStickPosition,
            renderFilters,
            renderLobbyComponent,
            lobbyComponents,
            renderLobbyPlayerItem,
        ]
    );

    // using a custom cell renderer component to make sure
    // the sticky components are rendered above the other items
    const CellRendererComponent = useCallback(
        ({ children, style, ...props }: CellRendererProps<LobbyListContent>) => {
            const isFilters = props.cellKey === 'filters';
            const isLeagues = props.cellKey === 'leagues';
            const adjustedStyle = {
                style,
                // set custom zIndex for the sticky components
                // this is needed to make sure the sticky components are rendered above the other items
                zIndex: cellKeyToZIndex[props.cellKey] || undefined,
                // on web we use "position: sticky" which needs the "top" property to be set
                // and represents the distance from the top of the list where it becomes sticky
                top: !isWeb ? 0 : isFilters ? LEAGUES_LIST_HEIGHT - TABS_HEIGHT : 0,
            };
            return (
                <StickyCellRendererComponent
                    {...props}
                    shouldBeSticky={isFilters || isLeagues}
                    style={adjustedStyle}
                    onLayout={event => {
                        // The launchpad has a different offset when is scrolled at the top
                        // (different margin top when is scrolled vs not scrolled)
                        // this offset needs to be added to each item bellow

                        // projection/position filters should stick below the launchpad
                        // this means that the stick position should be the initial y position of the filters
                        // minus the height of the launchpad (LEAGUES_LIST_HEIGHT)
                        // plus the height of the LeftSideTabBar (TABS_HEIGHT)
                        // plus the offset of the launchpad
                        if (isFilters) {
                            filtersStickPosition.value =
                                event.nativeEvent.layout.y -
                                LEAGUES_LIST_HEIGHT +
                                TABS_HEIGHT +
                                LAUNCHPAD_SCROLLED_BOTTOM_OFFSET;
                        }
                        // leagues should stick at top
                        // this means that the stick position should be the initial y position of the leagues
                        // plus the offset of the launchpad
                        if (isLeagues) {
                            leaguesStickPosition.value = event.nativeEvent.layout.y + LAUNCHPAD_SCROLLED_BOTTOM_OFFSET;
                        }

                        props.onLayout?.(event);
                    }}
                >
                    {children}
                </StickyCellRendererComponent>
            );
        },
        [filtersStickPosition, leaguesStickPosition]
    );

    return (
        <Box flex={1}>
            <AnimatedFlatList
                keyExtractor={keyExtractor}
                onScroll={onScroll}
                data={listToRender}
                contentContainerStyle={{ minHeight: listMinHeight }}
                windowSize={isWeb ? undefined : 5}
                // on web, the FlatList doesn't load the next page when scrolling
                initialNumToRender={isWeb ? listToRender.length : 20}
                maxToRenderPerBatch={10}
                ref={listRef}
                renderItem={renderListItem}
                CellRendererComponent={CellRendererComponent}
                ListFooterComponent={isLobby ? null : renderLeagueFooter}
                ListEmptyComponent={ListEmptyComponent}
                showsVerticalScrollIndicator={showScrollIndicator}
                // needed for sticky components to work on Android
                removeClippedSubviews={false}
            />
        </Box>
    );
};

const ListEmptyComponent = () => {
    const { height } = useWindowDimensions();
    return <SizedBox value={height} />;
};

const keyExtractor = (item: LobbyListContent, index: number) => {
    if (typeof item === 'string') {
        return `${item}`;
    } else if (typeof item === 'object' && 'orderingkey' in item) {
        return `LobbyItem-${item.orderingkey}`;
    } else {
        return `player-${item.id}-${index}`;
    }
};

/**
 * it returns the listMinHeight which is essential to avoid jumping of the list when it's length is recalculated
 * it returns the leaguesOffset which is the offset of the league cards list
 */
const useElementsDimensions = () => {
    const logoHeaderHeight = useLogoHeaderHeight();
    const insets = useSafeAreaInsets();

    const { height } = useWindowDimensions();
    const lobbyListHeight = height - insets.top - insets.bottom - PICKEM_TAB_BAR_HEIGHT - BOTTOM_TABS_HEIGHT;
    const listMinHeight = lobbyListHeight + logoHeaderHeight + SEARCH_CONTAINER_HEIGHT + LEAGUES_LIST_HEIGHT;

    const leaguesOffset = logoHeaderHeight + SEARCH_CONTAINER_HEIGHT + LAUNCHPAD_SCROLLED_BOTTOM_OFFSET;

    return { listMinHeight, leaguesOffset };
};

const AnimatedFlatList = Animated.createAnimatedComponent(FlatList<LobbyListContent>);

const cellKeyToZIndex: { [key: string]: number } = {
    filters: 101,
    leagues: 102,
} as const;
